/** @format */

import React, { useEffect, Fragment, createContext } from "react";
import { useState } from "react";
import AccountsOverviewView from "./AccountsOverview.view";
import VoiceGainAuthSvcApi from "../../../apis/VoiceGainAuthSvcApi";
import { Input, Button, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { ColumnProps } from "antd/lib/table";
import { IRegistrationFormProps } from "../../RegistrationContent/RegistrationForm/RegistrationForm.types";
import { IAccountsList } from "../MainContent.types";
import AppPropsService from "../../../services/AppPropsService";
import {
  fetchRecacheFetcher,
  useQueryAccounts,
  useQueryUsers,
} from "../../../apis/swr/fetchers";
import { KeyedMutator } from "swr";

interface IAccountsOverviewContainerProps {
  onSuccess: () => void;
  sitekey: string;
}

export const AccountsOverviewContainerContext = createContext<{
  mutateAccounts: KeyedMutator<IAccountsList[]> | undefined;
  updateNotesCache: (accountId: string, notes: string) => void;
}>({
  mutateAccounts: undefined,
  updateNotesCache: () => {},
});

const AccountsOverviewContainer: React.FC<IAccountsOverviewContainerProps> = ({
  onSuccess,
  sitekey,
}) => {
  const [siteKey, setSiteKey] = useState<string>("");
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [isRecaching, setIsRecaching] = useState<boolean>(false);
  const [notesCache, setNotesCache] = useState<{ [accountId: string]: string }>({});

  const {
    accounts,
    isLoading: isLoadingAccounts,
    mutate: mutateAccounts,
    isValidating: isValidatingAccounts,
  } = useQueryAccounts();

  // const {
  //   users,
  //   isLoading: isLoadingUsers,
  //   mutate: mutateUsers,
  //   isValidating: isValidatingUsers,
  // } = useQueryUsers();

  //Donwload accounts as CSV file
  const exportCsv = async () => {
    try {
      setIsDownloading(true);
      let response = await VoiceGainAuthSvcApi.getCsvAccount();
      if (response.ok) {
        let data = await response.text();
        const anchorElement = document.createElement("a");
        const blob = new Blob([data], { type: "text/plain" });
        const url = window.URL.createObjectURL(blob);
        anchorElement.download = "VoicegainAccounts.csv";
        anchorElement.href = url;
        anchorElement.dispatchEvent(
          new MouseEvent("click", {
            bubbles: true,
            cancelable: true,
            view: window,
          })
        );
      } else throw new Error("Unable to download CSV.");
    } catch (err) {
      if (err instanceof Error) {
        message.error(err.message);
      }
      console.log(err);
    } finally {
      setIsDownloading(false);
    }
  };

  //Update notes cache for a specific account
  const updateNotesCache = (accountId: string, notes: string) => {
    setNotesCache((prevCache) => ({
      ...prevCache,
      [accountId]: notes,
    }));
  };

  //Clear notes cache
  const clearNotesCache = () => {
    setNotesCache({});
  };

  //On refresh (mutate) accounts and users data
  const onRefresh = () => {
    setIsRefreshing(true);
    clearNotesCache();
    mutateAccounts();
    // mutateUsers();
  };

  //On recache
  const onRecache = async () => {
    setIsRecaching(true);

    const response = await fetchRecacheFetcher();

    setIsRecaching(false);
  };

  //Handle refreshing - set to false if isValidating (useSWR) is done
  //This is used for the loading spinner: we only want spinner to show when refresh is pressed.
  useEffect(() => {
    if (!isValidatingAccounts) setIsRefreshing(false);
  }, [isValidatingAccounts]);

  useEffect(() => {
    const getSiteKeyForRecaptcha = () => {
      setSiteKey(AppPropsService.getReCaptchaSiteKey());
    };
    getSiteKeyForRecaptcha();
  }, []);

  //Search function for table
  const getColumnSearchProps = (
    dataIndex: string,
    type?: "user" | "account"
  ): {
    filterDropdown: ColumnProps<IAccountsList>["filterDropdown"];
    filterIcon: ColumnProps<IAccountsList>["filterIcon"];
    onFilter: ColumnProps<IAccountsList>["onFilter"];
    render: ColumnProps<IAccountsList>["render"];
  } => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => {
      if (selectedKeys && setSelectedKeys) {
        return (
          <div style={{ padding: 8 }}>
            <Input
              placeholder={`Search`}
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() => handleSearch(selectedKeys, confirm)}
              style={{ width: 188, marginBottom: 8, display: "block" }}
            />
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys, confirm)}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90, marginRight: 8 }}
            >
              Search
            </Button>
            <Button
              onClick={() => handleReset(clearFilters, confirm)}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </div>
        );
      }
    },
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) => {
      // if (type === "user") {
      //   /*handle searching for users*/
      //   if (users) {
      //     const foundUsers = [...users].filter((user) => {
      //       if (user[dataIndex]) {
      //         if (
      //           user[dataIndex]
      //             .toString()
      //             .toLowerCase()
      //             .includes(value.toLowerCase())
      //         )
      //           return true;
      //         else return false;
      //       } else return false;
      //     });
      //     return foundUsers.some((user) => user.userId === record.creator);
      //   }
      // } else {
      if (record[dataIndex]) {
        return record[dataIndex]
          .toString()
          .toLowerCase()
          .includes(value.toString().toLowerCase());
      }
      // }
    },
    render: (text: string, record: IAccountsList) => {
      return (
        // <Fragment>
        //   <Highlighter
        //     highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
        //     searchWords={[searchText]}
        //     autoEscape
        //     textToHighlight={text ? text.toString() : "N/A"}
        //   />
        // </Fragment>
        text
      );
    },
  });

  const handleSearch = (selectedKeys, confirm) => {
    confirm();
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    confirm();
  };

  //Merge cached notes with accounts data
  const accountsWithCachedNotes = accounts
    ? accounts.map((account) => ({
        ...account,
        notes: notesCache[account.accountId] !== undefined 
          ? notesCache[account.accountId] 
          : account.notes,
      }))
    : [];

  return (
    <AccountsOverviewContainerContext.Provider value={{ mutateAccounts, updateNotesCache }}>
      <AccountsOverviewView
        accounts={accountsWithCachedNotes}
        // users={users ? users : []}
        isLoading={isRefreshing || isLoadingAccounts}
        isRecaching={isRecaching}
        // isLoadingUsers={isLoadingUsers}
        isDownloading={isDownloading}
        getColumnSearchProps={getColumnSearchProps}
        sitekey={siteKey}
        onSuccess={onSuccess}
        exportCsv={exportCsv}
        onRefresh={onRefresh}
        onRecache={onRecache}
      />
    </AccountsOverviewContainerContext.Provider>
  );
};

export default AccountsOverviewContainer;
